import type { ProductCardPrices } from "@shared/graphql.types";
import { useEffect, useState } from "react";

type UserType = "member" | "guest" | undefined;

type UseStrikethroughReturnType = {
  hasPromoPrice: boolean;
  promoPrice:
    | ProductCardPrices["member"]["promo"]
    | ProductCardPrices["guest"]["promo"]
    | null;
  price:
    | ProductCardPrices["member"]["regular"]
    | ProductCardPrices["guest"]["regular"]
    | null;
  guestPrice: ProductCardPrices["guest"]["regular"] | null;
  traditionalRetailPrice: ProductCardPrices["typical"]["regular"] | null;
  setActiveUserType: (userType: UserType) => void;
};

/**
 *
 * @param prices array of price values
 * @param forceMembership override activeType based on membership status
 * @returns
 */
export const useStrikethrough = (
  prices?: ProductCardPrices | null | undefined,
  forceMembership?: boolean
): UseStrikethroughReturnType => {
  const [activeUserType, setActiveUserTypeValue] = useState<UserType>(
    prices?.activeType
  );

  const setActiveUserType = (type: UserType) => {
    setActiveUserTypeValue(type);
  };
  useEffect(() => {
    if (typeof forceMembership === "boolean") {
      return setActiveUserTypeValue(forceMembership ? "member" : "guest");
    }
    setActiveUserTypeValue(prices?.activeType);
  }, [forceMembership, prices?.activeType]);

  if (!prices) {
    return {
      hasPromoPrice: false as const,
      promoPrice: null,
      price: null,
      guestPrice: null,
      traditionalRetailPrice: null,
      setActiveUserType
    };
  }

  const price = activeUserType === "member" ? prices.member : prices.guest;
  const hasPromo = Boolean(prices.member.promo && prices.guest.promo);

  return {
    hasPromoPrice: hasPromo,
    promoPrice: hasPromo ? price.promo : null,
    price: price.regular,
    guestPrice: prices.guest.regular,
    traditionalRetailPrice: prices.typical.regular,
    setActiveUserType
  };
};
